/**
 * /ko/prd — PRD (제품 요구사항 문서) concept page (Korean).
 * Links /ko/product-roadmap + /ko/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function KoPrd() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/prd"
      hreflangLocales={['ko']}
      documentTitle="PRD란? 제품 요구사항 문서 작성법과 양식 · Draftlize"
      documentDescription="PRD(제품 요구사항 문서)의 의미·구성·양식을 정리하고, 스프린트마다 낡지 않는 '살아있는 PRD' 만드는 법까지 설명합니다."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(31px, 4.2vw, 54px)', lineHeight: 1.1, margin: '12px 0 24px' }}>
            PRD(제품 요구사항 문서)<br />
            <em>란 무엇인가.</em>
          </h1>
          <p className="lede">
            <strong>PRD</strong>(Product Requirements Document／제품 요구사항 문서)는 <em>무엇을</em> 만들지, 그리고 <em>왜</em>인지를 정하는 문서입니다. 문제·범위·요구사항과 그 뒤의 결정을 모아 팀과 이해관계자가 같은 그림을 갖게 합니다. 약점은 그다음입니다——두세 스프린트면 PRD는 현실과 어긋나 <em>드리프트</em>합니다. 이 페이지는 구성과 양식, 그리고 드리프트를 막는 법을 다룹니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">버티는 PRD의 구성.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>문제와 목표</strong>——누구의 문제인지, 왜 지금인지, 어떤 성과를 노리는지.</li>
            <li><strong>범위(포함/제외)</strong>——의도적으로 넣지 않는 것.</li>
            <li><strong>요구사항</strong>——기능·비기능, 우선순위와 함께.</li>
            <li><strong>결정과 트레이드오프</strong>——무엇을 검토하고 왜 버렸는지.</li>
            <li><strong>미해결 질문·성공 기준</strong>.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            이 양식은 그대로 써도 됩니다. 가장 가치 있고 가장 빨리 낡는 건 4번——결정입니다. PRD는 <a href="/ko/product-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>로드맵</a>과 그 아래 결정의 '보이는 끝'일 뿐입니다.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">진짜 문제는 <em>PRD 드리프트.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            PRD는 승인된 날엔 옳습니다. 그다음 회의가 전제를 바꾸고 엣지 케이스가 결정을 뒤집는데——문서는 멈춰 있습니다. 12쪽짜리 문서를 손으로 따라 고치는 사람은 없습니다. 몇 주 뒤 AI 에이전트는 이미 성립하지 않는 스펙 위에서 작업을 시작합니다.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">요구사항이 카드가 된다</h3>
            <p>모든 요구사항과 결정이 7쪽의 한 줄이 아니라 참조 가능한 카드로——이유와 담당자를 담고 ID로 찾을 수 있습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">결정이 바뀌면 PRD가 알린다</h3>
            <p>나중 결정이 앞 결정을 뒤집으면 의존 카드——즉 해당 PRD 부분——가 자동으로 stale. 문서가 3주간 조용히 거짓말하지 않습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI 에이전트가 최신 스펙을 읽는다</h3>
            <p>Claude Code·Cursor가 MCP로 PRD를 읽습니다——최신이거나 명시적으로 stale. 상태를 아무도 모르는 정적 문서가 아닙니다.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          PRD는 승인된 날엔 옳다. 문제는 그 뒤 6주, 아무도 따라 고치지 않는다는 것이다.
        </blockquote>
        <cite>요구사항을 카드로 만들면, PRD가 스스로 "나 낡았다"고 알린다. 그게 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'PRD와 기획서/스펙의 차이는?', a: 'PRD는 "무엇을·왜"를 정하는 상위 문서, 스펙은 "어떻게 만들지"로 들어갑니다. PRD의 결정이 바뀌면 스펙도 바뀌어야 하며, 그 연동이 핵심입니다.' },
            { q: '누가 작성하나요?', a: '대개 PM이 엔지니어·디자인과 함께 작성합니다. 중요한 건 안의 결정을 나중에 되짚을 수 있고 이유가 남는 것입니다.' },
            { q: '분량은?', a: '문제·범위·요구사항·결정이 전달되면 충분합니다. 길이보다, 뒤집혔을 때 알아챌 수 있는 구조가 중요합니다.' },
            { q: '낡지 않게 하려면?', a: '요구사항과 결정을 고정된 문단이 아니라 연결된 카드로 만드세요. 결정이 바뀌면 Draftlize가 의존하는 곳을 자동으로 stale 처리합니다. 이어서 <link>.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>
                {f.a.replace(' 이어서 <link>.','')}
                {i === 3 && (<>{' 이어서 '}<a href="/ko/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>의사결정 기록</a>{'.'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">드리프트하지 않는 PRD를 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            PRD를 연결된 카드로 쓰면 각 요구사항이 참조 가능해집니다. 결정이 바뀌면 해당 부분을 자동 stale——PRD 드리프트는 끝입니다.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
